import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { tx } from './i18n';
import { storefrontOrigin } from './ProductPageLive';

// The store's menu as the store draws it, above the fields it is written in.
//
// The storefront shows its bar alone in a frame (its src/site/editBridge.js)
// and reads no menu of its own there: this hands it the menu being written
// (`sent`: the menu, the words by key in the language being written, and the
// entry whose panel is kept open), and again on every change, so the frame
// shows what is in the portal, saved or not. Pointing at a part of the menu
// tells the portal which entry, column, link or card it is (`onPick`). The
// frame says how tall the bar and its open panel are, and is made that tall.
// Nothing is saved through the frame.
//
// The frame stands in the page as it is: nothing is around it, it has no
// scroll of its own, and the page's scroll reads all of it. `view` is the
// page's: as a computer shows the bar (the width there is), or as a phone does.
const PHONE_WIDTH = 390;
// The store draws its bar as a row from this width; a narrower page shows the row made smaller.
const ROW_WIDTH = 960;
const FIRST_HEIGHT = 400;
const READY_MS = 15000;
const HAND_OVER_MS = 180;
const VIEW_MS = 60;

export default function MenuLive({ sent, onPick, view }) {
  const frame = useRef(null);
  const origin = useRef(null);
  const latest = useRef(sent);
  const picked = useRef(onPick);
  const shown = useRef(view);
  const handed = useRef(view);
  latest.current = sent;
  picked.current = onPick;
  shown.current = view;
  // 'waiting' until the page says it is drawn, 'ready', or 'silent' when it never does.
  const [state, setState] = useState('waiting');
  const [room, setRoom] = useState(0);
  // How tall the frame said it is, in each view: a view seen before is that tall at once.
  const [tall, setTall] = useState({});

  const tell = useCallback((message) => {
    if (origin.current && frame.current?.contentWindow) {
      frame.current.contentWindow.postMessage({ source: 'ph-portal', ...message }, origin.current);
    }
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.source !== frame.current?.contentWindow) return;
      const message = event.data;
      if (!message || message.source !== 'ph-storefront') return;
      origin.current = event.origin;
      if (message.type === 'menu-wanted') tell({ type: 'menu', ...latest.current });
      else if (message.type === 'ready') setState('ready');
      else if (message.type === 'menu-size' && Number(message.height) > 0) {
        const height = Number(message.height);
        setTall((current) => (current[shown.current] === height ? current : { ...current, [shown.current]: height }));
      } else if (message.type === 'pick') picked.current(message);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [tell]);

  // A page that never answers (a storefront without the preview) is said to be silent.
  useEffect(() => {
    if (state !== 'waiting') return undefined;
    const timer = window.setTimeout(() => setState((current) => (current === 'waiting' ? 'silent' : current)), READY_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  // Every change is handed over a moment after the last key, so typing stays
  // light; and again, sooner, when the frame changes its width, which draws
  // the bar as a drawer or as a row.
  useEffect(() => {
    if (state !== 'ready') return undefined;
    const wait = handed.current === view ? HAND_OVER_MS : VIEW_MS;
    const timer = window.setTimeout(() => { handed.current = view; tell({ type: 'menu', ...latest.current }); }, wait);
    return () => window.clearTimeout(timer);
  }, [sent, state, tell, view]);

  // The room there is for the frame.
  useEffect(() => {
    const element = frame.current?.parentElement;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const watch = new ResizeObserver(() => setRoom(element.clientWidth));
    watch.observe(element);
    setRoom(element.clientWidth);
    return () => watch.disconnect();
  }, []);

  // As tall as the bar and what it has open (a computer's panel, a phone's
  // drawer), however tall that is. A view not seen yet keeps the height the
  // frame has until it says its own, so it never falls back to a made-up one.
  const height = tall[view] ?? Object.values(tall)[0] ?? FIRST_HEIGHT;
  const small = view === 'computer' && room > 0 && room < ROW_WIDTH ? room / ROW_WIDTH : 1;
  const width = view === 'phone' ? `${PHONE_WIDTH}px` : small < 1 ? `${ROW_WIDTH}px` : '100%';
  return <section className={`xo-sitemenu-live is-${view}`} aria-label={tx('ui.hub.live.title')}>
    <iframe ref={frame} title={tx('ui.hub.live.title')} scrolling="no" src={`${storefrontOrigin()}/menu/preview/?ph-edit=1`}
      style={{ width, height: `${height}px`, zoom: small }} />
    {state === 'silent' && <p className="xo-hub-field-note">{tx('ui.hub.live.silent')}</p>}
  </section>;
}
